import { useLocation, useNavigate } from "react-router-dom";
import { MapPin, MessageCircle } from "lucide-react";
import { useLanguage } from "../i18n";
import { LOGO_LIGHT, LINKS } from "../data";

export default function Footer({ onValuation }) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const f = t.footer;

  const goPage = (path) => (e) => {
    e.preventDefault();
    if (location.pathname !== path) {
      navigate(path);
    } else if (window.__lenis) {
      window.__lenis.scrollTo(0, { duration: 1.2 });
    }
  };

  const goHome = (e) => {
    e.preventDefault();
    if (location.pathname !== "/") navigate("/");
    else if (window.__lenis) window.__lenis.scrollTo(0, { duration: 1.4 });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const linkCls = "text-white/70 transition-colors hover:text-white";

  const columns = [
    {
      title: f.explore,
      testid: "footer-col-explore",
      items: [
        { label: f.links.stays, href: "/strutture", testid: "footer-stays-link" },
        { label: f.links.experiences, href: "/esperienze", testid: "footer-experiences-link" },
        { label: f.links.guide, href: "/guide", testid: "footer-guide-link" },
      ],
    },
    {
      title: f.owners,
      testid: "footer-col-owners",
      items: [
        { label: f.ownerLinks.services, href: "/servizi", testid: "footer-services-link" },
        { label: f.ownerLinks.valuation, onClick: onValuation, testid: "footer-valuation-cta" },
        { label: f.ownerLinks.portal, href: LINKS.ownerPortal, external: true, testid: "footer-portal-link" },
      ],
    },
    {
      title: f.about,
      testid: "footer-col-about",
      items: [{ label: f.aboutLinks.chiSiamo, href: "/chi-siamo", testid: "footer-about-link" }],
    },
    {
      title: f.contact,
      testid: "footer-col-contact",
      items: [
        { label: "info@ogstay.com", href: "mailto:info@ogstay.com", external: true, testid: "footer-email-link" },
        { label: f.whatsapp, href: LINKS.whatsapp, external: true, testid: "footer-whatsapp-link" },
        { label: f.googleBusiness, href: LINKS.googleBusiness, external: true, testid: "footer-google-business-link" },
      ],
    },
  ];

  return (
    <footer data-testid="site-footer" className="bg-[#0A1F2B] py-20 text-white">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <a href="/" data-testid="footer-logo" onClick={goHome} aria-label="OGStay — Home" className="inline-flex items-center px-1 py-2">
              <img src={LOGO_LIGHT} alt="OGStay — Live Ogliastra. Stay Local." className="h-12 w-auto" />
            </a>
            <p className="mt-7 max-w-sm font-display text-xl italic text-sand">{f.tagline}</p>
            <p className="mt-4 text-sm text-white/50">{f.location}</p>
          </div>

          {columns.map((col) => (
            <nav key={col.testid} aria-label={col.title} data-testid={col.testid} className="lg:col-span-2">
              <h3 className="text-xs font-extrabold uppercase tracking-[0.25em] text-sky">{col.title}</h3>
              <ul className="mt-6 space-y-4 text-sm font-semibold">
                {col.items.map((item) => (
                  <li key={item.testid}>
                    {item.onClick ? (
                      <button data-testid={item.testid} onClick={item.onClick} className={linkCls}>
                        {item.label}
                      </button>
                    ) : item.external ? (
                      <a data-testid={item.testid} href={item.href} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 ${linkCls}`}>
                        {item.href === LINKS.whatsapp && <MessageCircle className="h-3.5 w-3.5" />}
                        {item.href === LINKS.googleBusiness && <MapPin className="h-3.5 w-3.5" />}
                        {item.label}
                      </a>
                    ) : (
                      <a data-testid={item.testid} href={item.href} onClick={goPage(item.href)} className={linkCls}>
                        {item.label}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-white/10 pt-8 text-xs font-semibold text-white/40">
          <span data-testid="footer-copyright">© 2026 OGStay · {f.rights}</span>
          <nav aria-label="Legal" className="flex items-center gap-5">
            <a data-testid="footer-privacy-link" href="/privacy-policy" onClick={goPage("/privacy-policy")} className="transition-colors hover:text-white">
              {f.legalLinks.privacy}
            </a>
            <a data-testid="footer-cookie-link" href="/cookie-policy" onClick={goPage("/cookie-policy")} className="transition-colors hover:text-white">
              {f.legalLinks.cookie}
            </a>
            <a data-testid="footer-terms-link" href="/termini" onClick={goPage("/termini")} className="transition-colors hover:text-white">
              {f.legalLinks.terms}
            </a>
          </nav>
          <span className="flex items-center gap-2">
            <span className="h-1.5 w-1.5 rounded-full bg-sky" />
            Ogliastra · Sardegna
          </span>
        </div>
      </div>
    </footer>
  );
}
